import { RoleSchema } from '@pf/contracts';
import { z } from 'zod';
import { type Session } from './console-contracts';

/**
 * The operator session plane, as `apps/api` actually serves it.
 *
 * SAD §11 describes this plane but `/ops/v1`'s OpenAPI document does not carry it, so there is no
 * generated schema to import — these are hand-written against `apps/api/src/routes/ops/index.ts`
 * and this file is the single place that knows its shape.
 *
 * Two properties of the real endpoint drive the whole design of the login screen, and both are
 * deliberate on the API's side:
 *
 * 1. **TOTP is sent with the password, in one request.** `POST /ops/v1/session` takes
 *    `{username, password, totp}` and `totp` is required. There is no "check the password first,
 *    then challenge" step — because a two-step flow has to answer "is this a real operator?"
 *    before the second factor, which is a user-enumeration oracle. Every failure here returns the
 *    same 401 with the same message for the same reason.
 * 2. **Signing out is a server call.** `DELETE /ops/v1/session` revokes the session and clears the
 *    cookie. The cookie is `HttpOnly` and scoped to `/ops`, so script cannot clear it — which
 *    makes calling that endpoint the only way to actually end an operator session, not an
 *    optimisation.
 *
 * The console used to model a *different* protocol here — `POST /auth/login` returning a
 * `TOTP_REQUIRED` challenge, then `POST /auth/totp` — which existed only in the MSW mock. Against
 * the real API every one of those paths 404s.
 */

/**
 * Whether the operator second factor is enforced.
 *
 * Temporarily `false` by decision: the login screen goes straight from password to session and
 * never shows the code step. The TOTP form, contracts and mock paths are all kept — flipping this
 * back on restores them — and the intended end state is a real setting, not this constant. Mirrors
 * `OPERATOR_TOTP_ENABLED` in `apps/api/src/ops/authenticator.ts` — flip both together.
 */
export const OPERATOR_TOTP_ENABLED = false;

/** `POST /ops/v1/session` request. One round trip; `totp` only while the second factor is on. */
export const OperatorLoginRequestSchema = z
  .object({
    username: z.string().min(3).max(64),
    password: z.string().min(1).max(512),
    totp: OPERATOR_TOTP_ENABLED
      ? z.string().regex(/^\d{6}$/, 'must be six digits')
      : z
          .string()
          .regex(/^\d{6}$/, 'must be six digits')
          .optional(),
  })
  .strict();

/** Inferred type of {@link OperatorLoginRequestSchema}. */
export type OperatorLoginRequest = z.infer<typeof OperatorLoginRequestSchema>;

/**
 * `POST /ops/v1/session` and `GET /ops/v1/session` response.
 *
 * Narrow on purpose — the API returns the operator's id, display name and role, and nothing else.
 * No username, no participant, no last-sign-in. {@link sessionFromOperator} is where that becomes the
 * console's own session model, and it fills the gaps rather than pretending the API sent them.
 */
export const OperatorSessionResponseSchema = z
  .object({
    operator: z
      .object({
        id: z.string().min(1),
        display_name: z.string().min(1),
        role: RoleSchema,
      })
      .strict(),
  })
  .strict();

/** Inferred type of {@link OperatorSessionResponseSchema}. */
export type OperatorSessionResponse = z.infer<typeof OperatorSessionResponseSchema>;

/**
 * `POST /ops/v1/session/password` request — the self-service change.
 *
 * The current password rides along as fresh proof; the new one obeys the same 8–128 policy as
 * the admin reset on `PATCH /ops/v1/operators/{id}`. Session-plane like login, so it lives here
 * and not in `@pf/contracts`.
 */
export const ChangePasswordRequestSchema = z
  .object({
    current_password: z.string().min(1).max(512),
    new_password: z.string().min(8).max(128),
  })
  .strict();

/** Inferred type of {@link ChangePasswordRequestSchema}. */
export type ChangePasswordRequest = z.infer<typeof ChangePasswordRequestSchema>;

/** `POST /ops/v1/session/password` response. On 200 every *other* session is already revoked. */
export const ChangePasswordResponseSchema = z.object({ changed: z.literal(true) }).strict();

/**
 * Widen an operator session response into the console's own identity model.
 *
 * The mirror image of `sessionFromCustomer`: two backends with two different notions of identity,
 * each adapted in exactly one place.
 */
export function sessionFromOperator(response: OperatorSessionResponse): Session {
  return {
    operator_id: response.operator.id,
    display_name: response.operator.display_name,
    // The API does not return an address for the operator, and inventing one from the id would put a
    // fake address in the sidebar. `null` is honest; `SessionSchema` allows it.
    email: null,
    area: 'operator',
    role: response.operator.role,
    // Operators belong to PaymentFlow, not to a participant. The field exists for the customer
    // plane, where it names the website the customer banks with.
    participant: 'PaymentFlow',
    account_ref: null,
    // While the second factor is enforced, reaching this point means a TOTP code was accepted;
    // with it switched off, the honest answer is that no second factor protects this session.
    totp_enabled: OPERATOR_TOTP_ENABLED,
    last_sign_in_at: null,
  };
}
